import React from 'react';
import {
  Box,
  Divider,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

/**
 * Componente para renderizar una pregunta de tipo escala de opinión (Opinion Scale)
 * Similar a E-NPS pero con rango personalizable y etiquetas en los extremos
 * 
 * @param {Object} props
 * @param {number} props.questionId - ID único de la pregunta
 * @param {string} props.typeQuestion - Tipo de la pregunta
 * @param {string} props.questionName - Texto de la pregunta
 * @param {string} [props.description] - Descripción adicional de la pregunta
 * @param {number} props.index - Índice de la pregunta en el array
 * @param {Array<number>} props.unansweredQuestions - Array de índices de preguntas sin responder
 * @param {Object} props.questionRef - Ref para scroll automático
 * @param {number|null} props.value - Valor actual seleccionado
 * @param {Function} props.onChange - Callback cuando cambia la selección
 * @param {Object} props.textBipolarBar - Configuración de la escala
 * @param {number} props.textBipolarBar.valueLeft - Valor mínimo de la escala
 * @param {number} props.textBipolarBar.valueRight - Valor máximo de la escala
 * @param {string} props.textBipolarBar.leftText - Etiqueta del extremo izquierdo
 * @param {string} props.textBipolarBar.rightText - Etiqueta del extremo derecho
 * @param {string} [props.primaryColor='#1976d2'] - Color primario del tema
 * @param {boolean} [props.showDivider=true] - Si se debe mostrar el divisor al final
 */
const OpinionScaleQuestion = ({
  questionId,
  typeQuestion,
  questionName,
  description,
  index,
  unansweredQuestions = [],
  questionRef,
  value,
  onChange,
  textBipolarBar = {
    valueLeft: 0,
    valueRight: 10,
    leftText: '',
    rightText: '',
  },
  primaryColor = '#1976d2',
  showDivider = true,
  hasOwnBold,
}) => {
  const hasError = unansweredQuestions.includes(index);
  
  // Generar array de números para los botones
  const minValue = Number(textBipolarBar.valueLeft);
  const maxValue = Number(textBipolarBar.valueRight);
  const scaleNumbers = Array.from(
    { length: maxValue - minValue + 1 },
    (_, i) => minValue + i
  );

  return (
    <>
      <FormLabel
        id={`${questionId}-${typeQuestion}`}
        style={{
          fontSize: '1.1',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '1.1em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
        ref={questionRef}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      {/* Etiquetas para móvil - vertical con flechas */}
      <Box
        display={{ xs: 'flex', md: 'none' }}
        flexDirection="column"
        width="100%"
        maxWidth="600px"
        mt={1}
        px={2}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.85rem',
            fontWeight: 500,
          }}
        >
          {textBipolarBar.valueLeft}
          <Box
            component="span"
            sx={{ fontSize: '1rem', color: primaryColor }}
          >
            ➔
          </Box>
          {textBipolarBar.leftText}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.85rem',
            fontWeight: 500,
          }}
        >
          {textBipolarBar.valueRight}
          <Box
            component="span"
            sx={{ fontSize: '1rem', color: primaryColor }}
          >
            ➔
          </Box>
          {textBipolarBar.rightText}
        </Typography>
      </Box>

      {/* Grupo de botones */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        mt={2}
        width="100%"
      >
        <ToggleButtonGroup
          value={value ?? null}
          exclusive
          aria-label="Opinion Scale"
          onChange={(event, newValue) => onChange(event, newValue, index)}
          sx={{
            flexWrap: 'wrap',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {scaleNumbers.map((num) => (
            <ToggleButton
              key={num}
              value={num}
              sx={{
                width: { xs: 36, sm: 42, md: 48 },
                height: { xs: 36, sm: 42, md: 48 },
                m: 0.5,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: primaryColor,
                  color: '#fff',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: primaryColor,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              {num}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Etiquetas para escritorio - horizontal */}
        <Box
          display={{ xs: 'none', md: 'flex' }}
          justifyContent="space-between"
          width="100%"
          maxWidth="600px"
          mt={1}
          px={2}
        >
          <Typography
            variant="caption"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {textBipolarBar.leftText}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {textBipolarBar.rightText}
          </Typography>
        </Box>
      </Box>

      {showDivider && (
        <Divider
          variant="middle"
          sx={{
            marginTop: '5px',
          }}
        />
      )}
    </>
  );
};

export default OpinionScaleQuestion;